import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdOutlineKeyboardArrowRight, MdDeleteOutline } from "react-icons/md";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import Categorys from "../components/Categorys";
import ProductsLoader from "../components/ProductsLoader";
import ProductDetailsModal from "../components/ProductDetailsModal";
import toast from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import {
  get_card_products,
  delete_card_product,
  messageClear,
  update_cart_item,
} from "../store/reducers/cardReducer";
import { get_banners } from "../store/reducers/homeReducer";
import { getProxiedUrl } from '../utils/imageUtils'

const Card = ({ sellerId, sellerName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo, supportInfo } = useSelector((state) => state.auth);
  const { banners } = useSelector((state) => state.home);
  const {
    card_products = [],
    successMessage,
    buy_product_item = 0,
    shipping_fee = 0,
    outofstock_products = [],
    isLoading = false,
    isInitialLoad = true,
  } = useSelector((state) => state.card);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = "4POS eCommerce Customer";
  }, []);

  const responsiveBanner = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  const handleBannerClick = (banner) => {
    // If banner has product information, show product modal
    if (banner.product) {
      setSelectedProduct(banner.product);
      setIsModalOpen(true);
    }
    // If banner has productId but no populated product, try to navigate by slug
    else if (banner.productSlug) {
      window.location.href = `/${sellerName}/product/details/${banner.productSlug}`;
    }
    // If link is a product slug, navigate to product page
    else if (banner.link) {
      window.location.href = `/${sellerName}/product/details/${banner.link}`;
    }
  };


    useEffect(() => {
      if (sellerId) {
        if (userInfo && userInfo.id) {
          dispatch(get_card_products({ userId: userInfo.id, sellerId }));
        } else {
          // For guest users, use sessionId
          const sessionId = sessionStorage.getItem('guestSessionId');
          if (sessionId) {
            dispatch(get_card_products({ userId: null, sellerId, sessionId }));
          }
        }
        dispatch(get_banners(sellerId));
      }
  }, [userInfo, sellerId]);
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
      if (sellerId) {
        if (userInfo && userInfo.id) {
          dispatch(get_card_products({ userId: userInfo.id, sellerId }));
        } else {
          // For guest users, use sessionId
          const sessionId = sessionStorage.getItem('guestSessionId');
          if (sessionId) {
            dispatch(get_card_products({ userId: null, sellerId, sessionId }));
          }
        }
      }
    }
  }, [successMessage]);

  const redirect = () => {
    // Consolidate sellers and include both in-stock and out-of-stock products
    const sellerIds = new Set([
      ...card_products.map(s => s.sellerId),
      ...outofstock_products.map(p => p.sellerId)
    ]);

    const consolidated = Array.from(sellerIds).map(sellerIdVal => {
      const seller = card_products.find(s => s.sellerId === sellerIdVal) || { products: [], sellerId: sellerIdVal, shopName: outofstock_products.find(p => p.sellerId === sellerIdVal)?.shopName };
      const sellerOutOfStock = outofstock_products.filter(p => p.sellerId === sellerIdVal);
      return {
        ...seller,
        products: [...seller.products, ...sellerOutOfStock]
      };
    });

    const addons = consolidated.flatMap((seller) =>
      seller.products.flatMap((product) => product.addons || [])
    );

    const checkoutSubtotal = consolidated.reduce((sum, seller) => {
      return sum + (seller.products || []).reduce((total, product) => {
        return total + calculateDiscountedPrice(product);
      }, 0);
    }, 0);

    navigate(`/${sellerName}/shipping`, {
      state: {
        products: consolidated,
        price: checkoutSubtotal,
        shipping_fee: shipping_fee,
        items: buy_product_item,
        addons: addons,
      },
    });
  };


  const calculateDiscountedPrice = (product) => {
    if (!product?.productInfo) return 0;

    // If discountPrice is available and valid, use it instead of recalculating
    if (product.discountPrice && product.discountPrice > 0) {
      // The stored discountPrice already includes addons from the product details page
      // So we don't need to add addons again
      return product.discountPrice;
    }

    // Fallback to original calculation if no discountPrice is stored
    const {
      price = 0,
      discount = 0,
      bulkPricing = [],
      currency = "NA",
    } = product.productInfo;
    const quantity = product.quantity || 1;

    // Calculate addons price
    let addonsPrice = 0;
    if (Array.isArray(product.addons)) {
      addonsPrice = product.addons.reduce((total, addon) => {
        return total + (typeof addon.price === 'number' ? addon.price : 0);
      }, 0);
    }

    // Calculate total price
    let totalPrice;

    // Handle unified bulk pricing if available and valid
    if (Array.isArray(bulkPricing) && bulkPricing.length > 0) {
      const validBulkPricing = bulkPricing.filter(
        (bp) => bp?.quantity && bp?.price && !isNaN(bp.quantity) && !isNaN(bp.price)
      );

      if (validBulkPricing.length > 0) {
        const sortedBulkPricing = [...validBulkPricing].sort(
          (a, b) => (b.quantity || 0) - (a.quantity || 0)
        );

        const applicableBulkPrice = sortedBulkPricing.find(
          (bp) => quantity >= (bp.quantity || 0)
        );

        if (applicableBulkPrice?.price) {
          // Mixed pack pricing logic: use largest available pack + remaining single items
          let remainingQuantity = quantity;
          let totalCost = 0;
          
          // Sort bulk pricing by quantity (largest first)
          const sortedBulkPricing = [...validBulkPricing].sort(
            (a, b) => (b.quantity || 0) - (a.quantity || 0)
          );
          
          // Use the largest pack that fits, but only once per iteration
          for (const bulkOption of sortedBulkPricing) {
            if (remainingQuantity >= bulkOption.quantity) {
              const packsNeeded = Math.floor(remainingQuantity / bulkOption.quantity);
              if (packsNeeded > 0) {
                totalCost += bulkOption.price * packsNeeded;
                remainingQuantity -= bulkOption.quantity * packsNeeded;
                break; // Use only the largest applicable pack
              }
            }
          }
          
          // Add remaining items at single item price
          if (remainingQuantity > 0) {
            totalCost += price * remainingQuantity;
          }
          
          totalCost += addonsPrice * quantity;
          return totalCost;
        }
      }
    }

    // If no bulk pricing applies, handle regular price with discount
    if (discount > 0) {
      // Calculate discount on the base price only
      const baseDiscount = (price * discount) / 100;
      const discountedBasePrice = price - baseDiscount;
      
      // Calculate total with discounted base price and addons
      totalPrice = (discountedBasePrice * quantity) + (addonsPrice * quantity);
    } else {
      // Regular price without discount
      totalPrice = (price * quantity) + (addonsPrice * quantity);
    }

    return totalPrice;
  };

  const CartItem = ({ product }) => {
    if (!product?.productInfo) return null;

    const currency = product.productInfo.currency || "NA";
    const discountedPrice = calculateDiscountedPrice(product);
    const originalPrice = product.productInfo.price || product.price || 0;

    const getBulkPricingInfo = () => {
        const { bulkPricing = [] } = product.productInfo;
        const validBulkPricing = bulkPricing.filter(
            (bp) =>
                bp?.quantity && bp?.price && !isNaN(bp.quantity) && !isNaN(bp.price)
        );

        if (validBulkPricing.length > 0) {
            const sortedBulkPricing = [...validBulkPricing].sort(
                (a, b) => (a.quantity || 0) - (b.quantity || 0)
            );

            return (
                <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700">Package Options:</p>
                    <div className="flex flex-col gap-1 mt-1">
                        {sortedBulkPricing.map((bp, index) => {
                            const perItemPrice = bp.perItemPrice || (bp.price / bp.quantity);
                            const description = bp.description || `${bp.quantity}-pack`;
                            return (
                                <div key={index} className="text-xs text-gray-600">
                                    {description}: {currency}
                                    {Number(bp.price || 0).toFixed(2)} total
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return null;
    };

    const removeAddon = async (index) => {
        try {
            const newAddons = [...product.addons];
            newAddons.splice(index, 1);
            
            const result = await dispatch(update_cart_item({
                cartId: product._id,
                addons: newAddons
            }));

            if (result.type === 'card/update_cart_item/fulfilled') {
                // Refresh cart data after successful update
                dispatch(get_card_products({
                    userId: userInfo.id,
                    sellerId: product.sellerId
                }));
            }
        } catch (error) {
            console.error('Error removing addon:', error);
        }
    };

    return (
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 transition-all hover:shadow-lg">
        <div className="flex items-start">
          <img
            className="w-20 h-20 object-cover rounded"
            src={getProxiedUrl(product.selectedImage || product.productInfo.images?.[0] || product.productInfo.image || "")}
            alt={product.productInfo.name || "Product image"}
            onError={(e) => {
              console.error('Image failed to load for product:', product.productInfo.name, 'Image URL:', product.selectedImage || product.productInfo.images?.[0]);
              e.target.src = '/images/no_image.jpg'; // Fallback image
            }}
          />
          <div className="ml-4 flex-grow">
            <h3 className="font-semibold text-lg">
              {product.productInfo.name || "Unnamed Product"}
            </h3>
            <p className="text-sm text-gray-500">
              Pack Size: {product.packSize || product.productInfo.packSize || "N/A"}
            </p>
            
            {/* Addons Section */}
            {product.addons && product.addons.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700">Selected Addons:</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {product.addons.map((addon, index) => (
                    <div
                      key={index}
                      className="bg-blue-100 text-blue-800 rounded-full px-2 py-1 flex items-center gap-1"
                    >
                      <span className="text-xs">
                        {addon.text || ""} ({currency}
                        {(addon.price || 0).toFixed(2)})
                      </span>
                      <button
                        onClick={() => removeAddon(index)}
                        className="text-blue-600 hover:text-blue-800 text-xs ml-1"
                        disabled={isLoading}
                        title="Remove addon"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bulk Pricing Section */}
            {getBulkPricingInfo()}

          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-green-600">
              {currency}
              {discountedPrice.toFixed(2)}
            </p>
            {discountedPrice < originalPrice && (
              <p className="text-sm text-gray-500 line-through">
                {currency}
                {originalPrice.toFixed(2)}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex justify-between items-center">
          <div className="flex items-center">
            <span className="px-4 py-2 font-medium text-gray-600">
              Quantity: {product.quantity || 1}
            </span>
          </div>
          <button
            onClick={() => dispatch(delete_card_product(product._id))}
            className="text-red-500 hover:text-red-700 transition-colors"
            disabled={isLoading}
          >
            <MdDeleteOutline size={24} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Headers sellerId={sellerId} sellerName={sellerName} />
      <section className="h-[220px] mt-6 bg-cover bg-no-repeat relative bg-left">
        <Carousel
          autoPlay={true}
          infinite={true}
          arrows={false}
          showDots={false}
          responsive={responsiveBanner}
          className="h-full"
        >
          {banners &&
            banners.length > 0 &&
            banners.map((b, i) => (
              <div 
                key={i} 
                className="h-full w-full cursor-pointer group"
                onClick={() => handleBannerClick(b)}
              >
                <img
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  src={b.banner}
                  alt=""
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
        </Carousel>
        <div className="absolute left-0 top-0 w-full h-full bg-[#2422228a]">
          <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
            <div className="flex flex-col justify-center gap-1 items-center h-full w-full text-white">
              <div className="flex justify-center items-center gap-2 text-2xl w-full">
                <Link to={`/${sellerName}`}>Home</Link>
                <span className="pt-2">
                  <MdOutlineKeyboardArrowRight />
                </span>
                <span>Cart</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Category Section */}
      <div className="my-4">
        <Categorys sellerId={sellerId} sellerName={sellerName} />
      </div>
      
      <section className="bg-gray-100">
                <div className="w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto py-16">
                  
                  {/* Checkout Progress Indicator */}
                  <div className="mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-semibold text-gray-800 mb-4">Checkout Progress</h2>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            1
                          </div>
                          <span className="ml-3 font-medium text-orange-600">Shopping Cart</span>
                        </div>
                        <div className="flex-1 h-1 bg-gray-200 mx-4">
                          <div className="h-1 bg-orange-500 w-1/3"></div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold text-sm">
                            2
                          </div>
                          <span className="ml-3 font-medium text-gray-500">Shipping Info</span>
                        </div>
                        <div className="flex-1 h-1 bg-gray-200 mx-4">
                          <div className="h-1 bg-gray-200 w-0"></div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold text-sm">
                            3
                          </div>
                          <span className="ml-3 font-medium text-gray-500">Payment</span>
                        </div>
                      </div>
                    </div>
                  </div>
                    {/* Warning for different seller products */}
                    {card_products.length > 0 && card_products[0]?.sellerId !== sellerId && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">
                                        Cart contains products from a different seller
                                    </h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        <p>You have items in your cart from a different seller. Please complete or clear your current cart before adding items from this seller.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {(card_products.length > 0 || outofstock_products.length > 0) ? (
                        <div className="flex flex-wrap gap-8">
                            <div className="w-[67%] md-lg:w-full">
                                <div className="pr-3 md-lg:pr-0">
                                    <div className="flex flex-col gap-3">
                                        {/* In Stock Products */}
                                        {card_products.length > 0 && (
                                            <div className="bg-white p-4 rounded-lg shadow-sm">
                                                <h2 className="text-lg font-semibold text-green-600">
                                                    In Stock Products ({card_products.reduce((total, seller) => 
                                                        total + seller.products.length, 0)})
                                                </h2>
                                                {card_products.map((seller, i) => (
                                                    <div key={i} className="bg-white p-4 rounded-lg shadow-sm">
                                                        <h3 className="text-lg font-semibold mb-4">
                                                            {seller.shopName}
                                                        </h3>
                                                        {seller.products.map((product, j) => (
                                                            <CartItem key={j} product={product} />
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Out of Stock Products */}
                                        {outofstock_products.length > 0 && (
                                            <div className="bg-white p-4 rounded-lg shadow-sm mt-4">
                                                <h2 className="text-lg font-semibold text-red-500">
                                                    Out of Stock Products ({outofstock_products.length})
                                                </h2>
                                                {outofstock_products.map((product, i) => (
                                                    <div key={i} className="bg-white p-4 rounded-lg shadow-sm mt-2">
                                                        <CartItem 
                                                            product={product} 
                                                            isOutOfStock={true}
                                                            maxStock={product.productInfo.stock}
                                                        />
                                                        <div className="text-red-500 text-sm mt-2">
                                                            Only {product.productInfo.stock} items available in stock
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

              <div className="w-[30%] md-lg:w-full">
                <div className="pl-3 md-lg:pl-0 md-lg:mt-5">
                  {(() => {
                    // Build consolidated seller list from both in-stock and out-of-stock buckets
                    const sellerIds = new Set([
                      ...card_products.map(s => s.sellerId),
                      ...outofstock_products.map(p => p.sellerId)
                    ]);
                    const consolidated = Array.from(sellerIds).map(sellerIdVal => {
                      const seller = card_products.find(s => s.sellerId === sellerIdVal) || { products: [], sellerId: sellerIdVal, shopName: outofstock_products.find(p => p.sellerId === sellerIdVal)?.shopName };
                      const sellerOutOfStock = outofstock_products.filter(p => p.sellerId === sellerIdVal);
                      const currency = seller.products[0]?.productInfo?.currency || sellerOutOfStock[0]?.productInfo?.currency || 'NA';
                      return { seller, sellerOutOfStock, currency };
                    });

                    return consolidated.map(({ seller, sellerOutOfStock, currency }, i) => {
                      const subtotalInStock = seller.products.reduce((total, product) => total + calculateDiscountedPrice(product), 0);
                      const subtotalOutOfStock = sellerOutOfStock.reduce((total, product) => total + calculateDiscountedPrice(product), 0);
                      const subtotal = subtotalInStock + subtotalOutOfStock;
                      const itemsCount = seller.products.reduce((count, p) => count + (p.quantity || 1), 0) + sellerOutOfStock.reduce((count, p) => count + (p.quantity || 1), 0);
                      const shipping = shipping_fee;
                      const total = subtotal + shipping;

                      return (
                        <div key={i} className="bg-white p-6 rounded-lg shadow-md">
                          <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                          <div className="flex justify-between items-center mb-4">
                            <span className="text-gray-600">Subtotal ({itemsCount} Items)</span>
                            <span className="font-semibold">
                              {currency}
                              {subtotal.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-4">
                            <span className="text-gray-600">Shipping Fee</span>
                            <span className="font-semibold">
                              {currency}
                              {shipping.toFixed(2)}
                            </span>
                          </div>
                          <div className="border-t pt-4 mt-4">
                            <div className="flex justify-between items-center mb-6">
                              <span className="text-lg font-semibold">Total</span>
                              <span className="text-xl font-bold text-green-600">
                                {currency}
                                {total.toFixed(2)}
                              </span>
                            </div>
                            <button
                              onClick={redirect}
                              className="w-full py-4 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                              disabled={isLoading}
                              title="Continue to shipping information"
                            >
                              {isLoading ? (
                                <>
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                  </svg>
                                  Proceed to Checkout ({buy_product_item} items)
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          ) : isLoading ? (
            <ProductsLoader isInitialLoad={isInitialLoad} isCart={true} />
          ) : (
            <div className="text-center py-20">
              <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
              <Link
                className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors inline-block"
                to={`/${sellerName}/shops`}
              >
                Start Shopping
              </Link>
            </div>
          )}
        </div>
      </section>
      <Footer sellerId={sellerId} sellerName={sellerName} />

      {/* Product Details Modal */}
      <ProductDetailsModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
        sellerName={sellerName}
        sellerId={sellerId}
        supportInfo={supportInfo}
      />
    </>
  );
};

export default Card;